/**
 * Credential redaction for everything a client ships to a log relay or
 * observability sink.
 *
 * Moved here from `@plerity/mobile-telemetry` (which re-exports it unchanged).
 *
 * Rule `mobile-security`: nothing in `console.*` / the log relay may carry a
 * credential. The relay captures console lines, failed-request URLs, error
 * bodies, and `reportError` envelopes verbatim, and any of those can carry a
 * bearer token, a JWT, a signed-URL signature, an OAuth `?code=`/`?token=`
 * query param, or a `scheme://user:password@host` DSN. These helpers scrub
 * the VALUE and keep the shape, so the line stays debuggable.
 *
 * React-Native-free (pure string ops) so `@plerity/mobile-net` and the
 * node-env test harness can import it via the `./redact` subpath.
 *
 * Both helpers NEVER throw (pure regex replacement over a string; any
 * non-string input is returned unchanged).
 */

/** Replacement marker for a scrubbed value. */
export const REDACTED = "[REDACTED]";

/**
 * Query / form parameter names whose VALUE is a credential. Matched
 * case-insensitively against the whole parameter name.
 */
const SENSITIVE_PARAM =
  "access_token|refresh_token|id_token|token|auth|authorization|api_key|api-key|apikey|key|secret|client_secret|password|passwd|pwd|signature|sig|x-amz-signature|x-amz-credential|x-amz-security-token|code|session|sessionid|jwt";

/** `?name=value` / `&name=value` with a sensitive name, anywhere in a string. */
const QUERY_PARAM_RE = new RegExp(`([?&#](?:${SENSITIVE_PARAM})=)[^&#\\s"'<>]*`, "gi");

/**
 * `scheme://user:password@host` — keep the user, drop the password.
 * The scheme is capped at 32 chars (real schemes are < 12): with an unbounded
 * `[a-z0-9+.-]*` this regex is O(n^2) on a long run like "a.a.a.…" (measured
 * ~27 s for 200 KB), and these helpers run over message/stack/body text.
 */
const USERINFO_RE = /\b([a-z][a-z0-9+.-]{0,31}:\/\/[^\s/@:"']+:)[^\s/@"']+@/gi;

/** A compact JWS/JWT (three base64url segments, header starts `eyJ`). */
const JWT_RE = /\beyJ[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]*/g;

/** `Bearer <token>` / `Basic <b64>` authorization values. */
const AUTH_SCHEME_RE = /\b(Bearer|Basic)\s+[A-Za-z0-9._~+/=-]{8,}/gi;

/** JSON-ish `"token": "value"` pairs for credential-named keys. */
const JSON_SECRET_RE =
  /("(?:password|passwd|token|access_?token|refresh_?token|id_?token|secret|client_?secret|api_?key|apiKey|authorization|accessToken|refreshToken|idToken|clientSecret)"\s*:\s*)"(?:[^"\\]|\\.)*"/gi;

/**
 * Scrub credential VALUES from an arbitrary log string (console line, error
 * message, response body, serialized envelope). Never throws.
 */
export function redactSecrets(text: string): string {
  // Non-strings pass through untouched (callers hand us `unknown`-derived
  // values); `String.prototype.replace` with a RegExp cannot throw on a string.
  if (typeof text !== "string" || text.length === 0) return text;
  return text
    .replace(JWT_RE, "[REDACTED_JWT]")
    .replace(AUTH_SCHEME_RE, (_m, scheme: string) => `${scheme} ${REDACTED}`)
    .replace(USERINFO_RE, `$1${REDACTED}@`)
    .replace(QUERY_PARAM_RE, `$1${REDACTED}`)
    .replace(JSON_SECRET_RE, `$1"${REDACTED}"`);
}

/**
 * Scrub credentials from a URL before it is logged: userinfo password,
 * credential-named query params (token, code, signature, X-Amz-*…), and any
 * JWT embedded in the path. Host + path stay intact for debugging.
 * Never throws.
 */
export function redactUrl(url: string): string {
  return redactSecrets(url);
}
